import { useEffect } from "react";
import Lenis from "lenis";
import Snap from "lenis/snap";
import "lenis/dist/lenis.css";
import { typedWords } from "./data";

const NAV_HEIGHT = 71;

// Scroll/reveal behaviors ported from the v3 design's DCLogic component:
// section reveals (.rv), staggered card reveals (.stag), animated stat
// counters (.statnum), nav scroll progress + active link, typed status text.
// Plus Lenis smooth scrolling with gentle section-by-section snapping.
export default function usePortfolioEffects() {
  useEffect(() => {
    const lenis = new Lenis({
      autoRaf: true,
      duration: 1.15,
      anchors: { offset: -NAV_HEIGHT },
    });

    // small distanceThreshold: only settle into a section when the user stops
    // right next to its boundary — never yank someone reading mid-section
    const snap = new Snap(lenis, { type: "proximity", duration: 0.9, distanceThreshold: "8%" });
    const snapTargets = document.querySelectorAll("header#top, section, footer#contact");
    // offsetTop chain ignores CSS transforms, so the .rv/.stag reveal
    // animations can't contaminate the measured section positions
    const layoutTop = (el) => {
      let t = 0;
      while (el) {
        t += el.offsetTop;
        el = el.offsetParent;
      }
      return t;
    };
    let removeSnaps = [];
    const buildSnaps = () => {
      removeSnaps.forEach((fn) => fn());
      removeSnaps = [];
      snapTargets.forEach((el) => {
        removeSnaps.push(snap.add(Math.max(0, layoutTop(el) - NAV_HEIGHT)));
      });
    };
    buildSnaps();
    // arriving with a hash (e.g. back from a case-study page via /#work):
    // scroll to the section once the page has painted
    let hashTimer;
    if (window.location.hash) {
      hashTimer = setTimeout(() => {
        const el = document.querySelector(window.location.hash);
        if (el) lenis.scrollTo(el, { offset: -NAV_HEIGHT });
      }, 350);
    }
    // section positions shift as fonts/images load — rebuild on any layout change
    let snapRebuildTimer;
    const bodyRo = new ResizeObserver(() => {
      clearTimeout(snapRebuildTimer);
      snapRebuildTimer = setTimeout(buildSnaps, 200);
    });
    bodyRo.observe(document.body);
    window.addEventListener("resize", buildSnaps);
    const io = new IntersectionObserver(
      (es) =>
        es.forEach((en) => {
          if (en.isIntersecting) {
            en.target.classList.add("in");
            io.unobserve(en.target);
          }
        }),
      { threshold: 0.06 }
    );
    document.querySelectorAll(".rv:not(.in)").forEach((n) => io.observe(n));

    const stagIo = new IntersectionObserver(
      (es) =>
        es.forEach((en) => {
          if (!en.isIntersecting) return;
          const siblings = Array.from(en.target.parentElement.children).filter((c) =>
            c.classList.contains("stag")
          );
          const idx = siblings.indexOf(en.target);
          en.target.style.transitionDelay = Math.max(idx, 0) * 70 + "ms";
          en.target.classList.add("in");
          stagIo.unobserve(en.target);
        }),
      { threshold: 0.15 }
    );
    document.querySelectorAll(".stag:not(.in)").forEach((n) => stagIo.observe(n));

    const statIo = new IntersectionObserver(
      (es) =>
        es.forEach((en) => {
          if (!en.isIntersecting) return;
          const el = en.target;
          const target = parseInt(el.dataset.target, 10);
          if (isNaN(target)) return;
          let start = null;
          const dur = 1100;
          const step = (ts) => {
            if (!start) start = ts;
            const p = Math.min((ts - start) / dur, 1);
            el.textContent = Math.round(target * (1 - Math.pow(1 - p, 3)));
            if (p < 1) requestAnimationFrame(step);
            else el.textContent = target;
          };
          requestAnimationFrame(step);
          statIo.unobserve(en.target);
        }),
      { threshold: 0.5 }
    );
    document.querySelectorAll(".statnum").forEach((n) => statIo.observe(n));

    const onScroll = () => {
      const h = document.documentElement;
      const pct = (h.scrollTop / (h.scrollHeight - h.clientHeight)) * 100;
      const bar = document.getElementById("navprogress");
      if (bar) bar.style.width = (isFinite(pct) ? pct : 0) + "%";
      const links = document.querySelectorAll(".navlink");
      let current = null;
      document.querySelectorAll(".section, header#top").forEach((s) => {
        if (s.getBoundingClientRect().top < 140) current = s.id;
      });
      links.forEach((l) =>
        l.classList.toggle("active", Boolean(current) && l.getAttribute("href") === "#" + current)
      );
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();

    let wi = 0;
    const typeInterval = setInterval(() => {
      wi = (wi + 1) % typedWords.length;
      const el = document.getElementById("typed");
      if (el) el.textContent = typedWords[wi];
    }, 3200);

    return () => {
      io.disconnect();
      stagIo.disconnect();
      statIo.disconnect();
      window.removeEventListener("scroll", onScroll);
      clearInterval(typeInterval);
      bodyRo.disconnect();
      clearTimeout(snapRebuildTimer);
      clearTimeout(hashTimer);
      window.removeEventListener("resize", buildSnaps);
      snap.destroy();
      lenis.destroy();
    };
  }, []);
}
